import { useQuery } from '@tanstack/react-query';
import { Alert, Card, DatePicker, Flex, Skeleton, Space, Statistic, Tag, Tooltip, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { api } from '../../api';
import { diaDeHoje, somarDias } from './serieDoRitmo';
import { preverConclusao } from './previsao';

/**
 * Quando os comentários terminam, no ritmo medido.
 *
 * **Fica no alto, logo depois dos indicadores.** Sai do Ritmo, mas não mora com
 * ele: ninguém rola até o fim da página para saber quando o trabalho acaba. A
 * leitura da tela vira "quanto já foi, quando termina, o que fazer agora".
 *
 * **Cartão próprio, e não mais uma coluna na linha de indicadores.** Precisa do
 * seu próprio período, e período escolhido dentro do cartão do Ritmo seria lido
 * como a janela do gráfico - duas escolhas de tempo juntas viram uma só na
 * cabeça de quem usa.
 *
 * **Enxuto, porque está no alto.** Os números todos continuam à vista; o que
 * saiu foi a explicação em volta deles, que mora nos comentários de
 * `previsao.ts`. Data prevista sozinha seria oráculo - ninguém saberia de que
 * amostra saiu, nem teria como discordar -, então o que falta, o ritmo e o
 * tamanho da amostra ficam ao lado dela.
 *
 * A previsão respeita os filtros da tela: filtrando por uma prova, ela responde
 * quando **aquela** prova termina.
 */

/** Quando o time começou a escrever para valer. É a origem padrão da medição. */
const INICIO_PADRAO = '2026-08-08';

/**
 * O contrato do ritmo aceita no máximo 365 dias.
 *
 * Sem este limite, escolher uma data mais antiga devolveria uma série cortada
 * sem aviso, e o ritmo sairia menor do que foi - com a previsão errando para
 * mais e ninguém sabendo por quê. O seletor barra a data em vez de deixar a
 * conta mentir.
 */
const JANELA_MAXIMA = 365;

const paraTexto = (valor: Dayjs) => valor.format('YYYY-MM-DD');
const emBrasileiro = (dia: string) => dayjs(dia).format('DD/MM/YYYY');
/** Forma curta, para a linha da amostra não virar um parágrafo de datas. */
const curto = (dia: string) => dayjs(dia).format('DD/MM/YY');

export function PrevisaoDeConclusao({
  restantes,
  comSugestao,
  filtros,
  de,
  ate,
  onMudarPeriodo,
  compacto,
}: {
  /**
   * Peças que **nenhuma pessoa assinou** ainda, no filtro atual da tela.
   *
   * A peça que a máquina escreveu e ninguém revisou continua contando como
   * restante, e isso é decisão, não descuido: a linha de chegada do produto é o
   * comentário que o especialista assina, não o que a máquina redigiu. Sugestão
   * pendente é trabalho por fazer, e descontá-la aqui levaria a data a despencar
   * numa noite de Comentário Assistido sem ninguém ter revisado nada.
   *
   * O que faz a conta fechar é a outra ponta responder à mesma pergunta: o ritmo
   * embaixo conta **peças assinadas por dia**, escritas e validadas juntas (ver
   * `previsao.ts`). Enquanto ele contava só as escritas, o estoque tratava a peça
   * revisada como pronta e a velocidade não via a revisão acontecer.
   */
  restantes: number;
  /** Quantas dessas já têm rascunho da máquina: são as mais baratas de fechar. */
  comSugestao: number;
  filtros: Record<string, string | undefined>;
  de: string;
  ate: string;
  onMudarPeriodo: (de: string, ate: string) => void;
  compacto: boolean;
}) {
  const hoje = diaDeHoje();

  /*
   * A rota conta dias para trás a partir de agora, então pedimos o suficiente
   * para cobrir o início escolhido e recortamos o período na conta. Buscar a
   * mais é barato - é uma linha por dia e por pessoa.
   */
  const dias = Math.min(
    JANELA_MAXIMA,
    Math.max(1, dayjs(hoje).diff(dayjs(de), 'day') + 1),
  );

  const ritmo = useQuery({
    queryKey: ['progresso-previsao', filtros, dias],
    queryFn: () => api.ritmoDoProgresso({ ...filtros, dias }),
  });

  const seletor = (
    <DatePicker.RangePicker
      size="small"
      allowClear={false}
      style={{ width: compacto ? '100%' : 260 }}
      value={[dayjs(de), dayjs(ate)]}
      format="DD/MM/YYYY"
      // Nem o futuro, nem além do que a rota alcança: data que a série não
      // cobre viraria ritmo falso, não erro.
      disabledDate={(data) =>
        data.isAfter(dayjs(hoje), 'day') ||
        data.isBefore(dayjs(somarDias(hoje, -JANELA_MAXIMA + 1)), 'day')
      }
      presets={[
        { label: 'Desde o começo', value: [dayjs(INICIO_PADRAO), dayjs(hoje)] },
        { label: 'Últimos 7 dias', value: [dayjs(somarDias(hoje, -6)), dayjs(hoje)] },
        { label: 'Últimos 30 dias', value: [dayjs(somarDias(hoje, -29)), dayjs(hoje)] },
      ]}
      onChange={(valores) => {
        const [inicio, fim] = valores ?? [];
        if (inicio && fim) onMudarPeriodo(paraTexto(inicio), paraTexto(fim));
      }}
    />
  );

  const previsao = preverConclusao({ dados: ritmo.data ?? [], de, ate, restantes, hoje });

  return (
    <Card
      size="small"
      title="Previsão de conclusão"
      // No celular o seletor vai para baixo do título: na linha dele, um campo
      // de duas datas não sobra espaço para o nome do cartão.
      extra={compacto ? null : seletor}
    >
      {compacto ? <div style={{ marginBottom: 12 }}>{seletor}</div> : null}

      {ritmo.isPending ? (
        <Skeleton active paragraph={{ rows: 1 }} />
      ) : previsao.motivo === 'concluido' ? (
        <Alert type="success" showIcon title="Nada a escrever neste filtro." />
      ) : previsao.motivo === 'sem-ritmo' ? (
        <Alert
          type="warning"
          showIcon
          title={`Ninguém escreveu nem revisou entre ${curto(de)} e ${curto(ate)} - sem ritmo, não há data.`}
        />
      ) : (
        <Flex vertical gap={8}>
          <Flex gap={compacto ? 16 : 32} wrap align="flex-start">
            <Statistic
              title="Conclusão prevista"
              value={emBrasileiro(previsao.dia!)}
              suffix={
                <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                  em {previsao.diasRestantes} dia(s)
                </Typography.Text>
              }
            />
            {/*
              Os números da conta ao lado da data. Sem eles a previsão é
              oráculo: ninguém sabe de que amostra ela saiu, nem tem como
              discordar.
            */}
            <Statistic
              title="Peças a escrever"
              value={previsao.restantes}
              valueStyle={{ fontSize: 20 }}
              suffix={
                comSugestao > 0 ? (
                  <Tooltip title="Já existem como sugestão da máquina, esperando revisão. Custam menos que escrever do zero, e a data acima não desconta nada por elas: o desconto aparece sozinho no ritmo, que conta o dia de revisão como conta o de redação.">
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                      {comSugestao} com sugestão
                    </Typography.Text>
                  </Tooltip>
                ) : undefined
              }
            />
            <Statistic
              title="Ritmo medido"
              value={previsao.porDia.toFixed(1)}
              suffix={
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  peças/dia
                </Typography.Text>
              }
              valueStyle={{ fontSize: 20 }}
            />
          </Flex>

          {/*
            A amostra, numa linha. O porquê de os dias parados entrarem na conta
            mora no comentário de `previsao.ts`: na tela basta dizer que entram,
            que é o que muda a leitura do número.
          */}
          <Space size={8} wrap>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {previsao.pecasNoPeriodo} peças escritas ou validadas em {previsao.diasMedidos} dias (
              {curto(de)} a {curto(ate)}), dias parados incluídos.
            </Typography.Text>
            {/*
              O aviso encosta no número que ele qualifica, em vez de virar caixa
              embaixo: com poucos dias medidos, um dia cheio ou parado ainda move
              a data em semanas.
            */}
            {previsao.diasMedidos < 7 ? (
              <Tooltip title="Menos de uma semana medida: um dia cheio ou parado ainda move muito a data.">
                <Tag color="warning" style={{ marginInlineEnd: 0 }}>
                  amostra curta
                </Tag>
              </Tooltip>
            ) : null}
          </Space>
        </Flex>
      )}
    </Card>
  );
}
